import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { usePathname } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { LucideIcon } from 'lucide-react';

/**
 * How loud a badge is. `default` is the quiet grey pill — a tally the user may
 * read at leisure. `alert` is red: outstanding work that the IM owns, or news
 * they have not seen (SEP-210).
 */
export type SidebarBadgeTone = 'default' | 'alert';

/**
 * The tooltip of a sidebar control. It shows on hover and on keyboard focus of
 * the nearest `group` ancestor.
 *
 * The tooltip is `fixed` with no offsets, so it stays at its static position
 * and the scroll box of the nav does not clip it. The host must be a
 * `flex items-center justify-start` element: the tooltip then starts at the
 * left edge of the content box of the host, centred on the vertical axis.
 * `className` moves it to the right of the host with a `translate-x-*` class.
 */
export function SidebarTooltip({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) {
    return (
        <span
            role="tooltip"
            aria-hidden="true"
            className={cn(
                'pointer-events-none fixed z-50 hidden whitespace-nowrap rounded-[6px] px-2 py-1',
                'border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-raised))]',
                'text-[12px] leading-4 font-medium text-[rgb(var(--color-text-primary))] shadow-md',
                'group-hover:inline-flex group-focus-visible:inline-flex items-center gap-1.5',
                className,
            )}
        >
            {children}
        </span>
    );
}

interface SidebarRowProps {
    icon: LucideIcon;
    label: string;
    href: string;
    badge?: string | number;
    badgeTone?: SidebarBadgeTone;
    /** Legacy / equivalent routes that should also light this row as active. */
    aliases?: string[];
    /** True when the sidebar is the 48px icon rail. */
    collapsed?: boolean;
    className?: string;
}

export default function SidebarRow({
    icon: Icon,
    label,
    href,
    badge,
    badgeTone = 'default',
    aliases,
    collapsed = false,
    className,
}: SidebarRowProps) {
    const pathname = usePathname();
    const matchesRoute = (route: string) => pathname === route || pathname.startsWith(route + '/');
    const selected = matchesRoute(href) || (aliases ?? []).some(matchesRoute);
    const hasBadge = badge !== undefined;
    // An alert badge keeps its red whether or not the row is selected: the
    // point of the colour is that it carries across the whole sidebar at a
    // glance.
    const alert = badgeTone === 'alert';

    return (
        <Link
            to={href}
            aria-current={selected ? 'page' : undefined}
            aria-label={collapsed ? (hasBadge ? `${label}, ${badge}` : label) : undefined}
            className={cn(
                'group relative flex w-full items-center justify-start text-left',
                collapsed ? 'h-8 pl-3' : 'h-7 gap-2 px-2',
                'rounded-[6px] outline-none transition-colors duration-card-hover',
                'focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)]',
                // Active = soft fill + weight (no left stripe, no persona accent on rows).
                selected
                    ? 'bg-[rgb(var(--color-sidebar-hover)/0.06)]'
                    : 'hover:bg-[rgb(var(--color-sidebar-hover)/0.035)]',
                className,
            )}
        >
            <span
                className={cn(
                    'relative flex-shrink-0 transition-colors',
                    selected
                        ? 'text-[rgb(var(--color-text-primary))]'
                        : 'text-[rgb(var(--color-text-secondary))] group-hover:text-[rgb(var(--color-text-primary))]',
                )}
            >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {collapsed && hasBadge && (
                    <span
                        aria-hidden="true"
                        className={cn(
                            'absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full',
                            alert
                                ? 'bg-[rgb(var(--color-rag-red))]'
                                : 'bg-[rgb(var(--color-text-tertiary))]',
                        )}
                    />
                )}
            </span>
            <span
                className={cn(
                    collapsed ? 'sr-only' : 'flex-1 truncate text-[12.5px] leading-4',
                    selected
                        ? 'font-semibold text-[rgb(var(--color-text-primary))]'
                        : 'font-medium text-[rgb(var(--color-text-secondary))] group-hover:text-[rgb(var(--color-text-primary))]',
                )}
            >
                {label}
            </span>
            {hasBadge && !collapsed && (
                <span
                    className={cn(
                        'min-w-4 rounded-[4px] px-1 text-center text-[10px] leading-4 font-medium tabular-nums',
                        alert
                            ? 'bg-[rgb(var(--color-rag-red)/0.12)] text-[rgb(var(--color-rag-red))]'
                            : selected
                              ? 'bg-[rgb(var(--color-text-primary)/0.08)] text-[rgb(var(--color-text-primary))]'
                              : 'bg-[rgb(var(--color-text-primary)/0.06)] text-[rgb(var(--color-text-tertiary))]',
                    )}
                >
                    {badge}
                </span>
            )}
            {collapsed && (
                // The rail row starts at 4px, with a 12px left padding. A
                // 38px shift puts the tooltip 6px to the right of the rail.
                <SidebarTooltip className="translate-x-[38px]">
                    <span>{label}</span>
                    {hasBadge && (
                        <span
                            className={cn(
                                'tabular-nums text-[11px]',
                                alert
                                    ? 'text-[rgb(var(--color-rag-red))]'
                                    : 'text-[rgb(var(--color-text-tertiary))]',
                            )}
                        >
                            {badge}
                        </span>
                    )}
                </SidebarTooltip>
            )}
        </Link>
    );
}
